import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  ArrowLeft, 
  Edit, 
  Check, 
  X, 
  Clock, 
  Calendar, 
  User, 
  FileText,
  AlertCircle,
  CheckCircle,
  XCircle,
  RefreshCw,
  Download,
  Play,
  Pause,
  Square,
} from 'lucide-react';
import { timesheetAPI } from '../services/api';
import { exportTimesheetPdf } from '../utils/timesheetPdf';
import { format, isValid } from 'date-fns';
import toast from 'react-hot-toast';

const TimesheetDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  
  // Helper function to safely format dates
  const safeFormatDate = (dateString, formatString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      if (isValid(date)) {
        return format(date, formatString);
      }
      return 'Invalid Date';
    } catch (error) {
      console.error('Date formatting error:', error);
      return 'Invalid Date';
    }
  };
  
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [approvalAction, setApprovalAction] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [currentTime, setCurrentTime] = useState(new Date());
  const [isTimerRunning, setIsTimerRunning] = useState(false);
  const [timerStartTime, setTimerStartTime] = useState(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [selectedDay, setSelectedDay] = useState(null);
  const [showDayDetails, setShowDayDetails] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Real-time clock update
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Timer functionality
  useEffect(() => {
    let interval = null;
    if (isTimerRunning) {
      interval = setInterval(() => {
        setElapsedTime(prev => prev + 1);
      }, 1000);
    } else if (!isTimerRunning && elapsedTime !== 0) {
      clearInterval(interval);
    }
    return () => clearInterval(interval);
  }, [isTimerRunning, elapsedTime]);

  const startTimer = () => {
    setIsTimerRunning(true);
    setTimerStartTime(new Date());
  };

  const pauseTimer = () => {
    setIsTimerRunning(false);
  };

  const stopTimer = () => {
    setIsTimerRunning(false);
    setElapsedTime(0);
    setTimerStartTime(null);
  };

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleDayClick = (day) => {
    setSelectedDay(day);
    setShowDayDetails(true);
  };

  const closeDayDetails = () => {
    setShowDayDetails(false);
    setSelectedDay(null);
  };

  const { data: timesheet, isLoading, error } = useQuery(
    ['timesheet', id, user?.role],
    () => {
      if (user?.role === 'SUPER_ADMIN' || user?.role === 'ADMIN') {
        return timesheetAPI.getAdminTimesheetById(id);
      } else {
        return timesheetAPI.getTimesheetById(id);
      }
    },
    {
      retry: false, // Don't retry on error (especially for 404s)
      onError: (error) => {
        const statusCode = error.response?.status;
        const isNotFound = statusCode === 404 || statusCode === 400;
        const isServerError = statusCode === 500 || statusCode >= 500;
        
        if (isNotFound) {
          toast.error('Timesheet not found. It may have been deleted.');
        } else if (isServerError) {
          toast.error('Server error occurred. Please try again later.');
        } else {
          toast.error('Failed to load timesheet details.');
        }
      },
      select: (response) => {
        const data = response.data;
        
        if (!data) {
          return null;
        }
        
        // Handle nested data structure
        const actualData = data.data || data;
        
        // Ensure we have a valid timesheet object with all required fields
        const timesheetData = {
          id: actualData.id,
          employee: actualData.employee || null,
          startDate: actualData.startDate,
          endDate: actualData.endDate,
          totalHours: actualData.totalHours || 0,
          status: actualData.status,
          description: actualData.description || '',
          approvedBy: actualData.approvedBy || null,
          approvedAt: actualData.approvedAt || null,
          rejectionReason: actualData.rejectionReason || null,
          revocationReason: actualData.revocationReason || null,
          revokedBy: actualData.revokedBy || null,
          createdAt: actualData.createdAt || null,
          updatedAt: actualData.updatedAt || null,
          submittedAt: actualData.submittedAt || null,
          entries: []
        };
        
        if (actualData.entries && Array.isArray(actualData.entries)) {
          // Map backend field names to frontend expected field names
          const mappedEntries = actualData.entries.map(entry => {
            return {
              id: entry.id,
              date: entry.date ? new Date(entry.date).toISOString().split('T')[0] : 
                    entry.entryDate ? new Date(entry.entryDate).toISOString().split('T')[0] : null,
              hours: entry.hours || entry.hoursWorked || 0,
              project: entry.project?.name || entry.projectName || entry.project || '',
              description: entry.task || entry.taskDescription || 'No description provided',
              // Add any missing fields with defaults
              taskName: entry.taskName || '',
              notes: entry.notes || '',
              startTime: entry.startTime || '',
              endTime: entry.endTime || ''
            };
          });
          
          timesheetData.entries = mappedEntries;
        }
        
        return timesheetData;
      },
      enabled: !!id && !!user,
    }
  );

  const approveMutation = useMutation(
    () => {
      if (user?.role === 'SUPER_ADMIN' || user?.role === 'ADMIN') {
        return timesheetAPI.approveTimesheet(id);
      } else {
        return timesheetAPI.approveTimesheet(id);
      }
    },
    {
      onSuccess: () => {
        toast.success('Timesheet approved successfully');
        queryClient.invalidateQueries(['timesheet', id, user?.role]);
        setShowApprovalModal(false);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to approve timesheet');
      },
    }
  );

  const rejectMutation = useMutation(
    (reason) => {
      if (user?.role === 'SUPER_ADMIN' || user?.role === 'ADMIN') {
        return timesheetAPI.rejectTimesheet(id, reason);
      } else {
        return timesheetAPI.rejectTimesheet(id, reason);
      }
    },
    {
      onSuccess: () => {
        toast.success('Timesheet rejected successfully');
        queryClient.invalidateQueries(['timesheet', id, user?.role]);
        setShowApprovalModal(false);
        setRejectionReason('');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to reject timesheet');
      },
    }
  );

  const getStatusIcon = (status) => {
    switch (status) {
      case 'DRAFT':
        return <Clock className="w-5 h-5 text-gray-500" />;
      case 'SUBMITTED':
        return <AlertCircle className="w-5 h-5 text-yellow-500" />;
      case 'PENDING':
        return <RefreshCw className="w-5 h-5 text-orange-500" />;
      case 'REUPDATED':
        return <RefreshCw className="w-5 h-5 text-blue-500" />;
      case 'APPROVED':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'REJECTED':
        return <XCircle className="w-5 h-5 text-red-500" />;
      default:
        return <Clock className="w-5 h-5 text-gray-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'DRAFT':
        return 'bg-gray-100 text-gray-800';
      case 'SUBMITTED':
        return 'bg-yellow-100 text-yellow-800';
      case 'PENDING':
        return 'bg-orange-100 text-orange-800';
      case 'REUPDATED':
        return 'bg-blue-100 text-blue-800';
      case 'APPROVED':
        return 'bg-green-100 text-green-800';
      case 'REJECTED':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const calculateTotalHours = (entries) => {
    return entries?.reduce((total, entry) => total + (entry.hours || 0), 0) || 0;
  };

  const handleApproval = (action) => {
    setApprovalAction(action);
    setShowApprovalModal(true);
  };

  const confirmApproval = () => {
    if (approvalAction === 'approve') {
      approveMutation.mutate();
    } else if (approvalAction === 'reject') {
      if (!rejectionReason.trim()) {
        toast.error('Please provide a reason for rejection');
        return;
      }
      rejectMutation.mutate(rejectionReason);
    }
  };


  const handleExport = async () => {
    try {
      await exportTimesheetPdf(timesheet, { template: 'classic' });
    } catch (e) {
      console.error('PDF export failed', e);
      toast.error('Failed to export PDF');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  if (error) {
    const statusCode = error.response?.status;
    const isNotFound = statusCode === 404 || statusCode === 400;
    const isServerError = statusCode === 500 || statusCode >= 500;
    const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message || 'Failed to load timesheet details';
    const isDeleted = errorMessage.toLowerCase().includes('not found') || errorMessage.toLowerCase().includes('deleted');
    
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center max-w-md">
          <AlertCircle className="mx-auto h-16 w-16 text-red-500 mb-4" />
          <h3 className="mt-2 text-lg font-medium text-gray-900">
            {isNotFound || isDeleted ? 'Timesheet Not Found' : isServerError ? 'Server Error' : 'Error Loading Timesheet'}
          </h3>
          <p className="mt-2 text-sm text-gray-500">
            {isDeleted || isNotFound 
              ? 'This timesheet may have been deleted or does not exist. Please navigate back to view your other timesheets.'
              : isServerError
              ? 'An internal server error occurred while loading the timesheet. Please try again later or contact support if the problem persists.'
              : errorMessage}
          </p>
          <div className="mt-6 flex justify-center space-x-4">
            <button
              onClick={() => navigate(-1)}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Go Back
            </button>
            <button
              onClick={() => navigate('/timesheets')}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700"
            >
              View My Timesheets
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!timesheet) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <FileText className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Timesheet not found</h3>
          <p className="mt-1 text-sm text-gray-500">The requested timesheet could not be found.</p>
        </div>
      </div>
    );
  }

  // Separate Mobile View
  if (isMobile) {
    return (
      <div className="min-h-screen bg-gray-50 pb-6">
        {/* Mobile Header */}
        <div className="bg-white border-b border-gray-200 sticky top-0 z-40">
          <div className="px-4 py-3">
            <div className="flex items-center justify-between mb-3">
              <button
                onClick={() => navigate(-1)}
                className="p-2.5 -ml-2 text-gray-600 active:text-gray-900 transition-colors rounded-lg active:bg-gray-100 touch-manipulation"
              >
                <ArrowLeft className="w-6 h-6" />
              </button>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleExport}
                  className="p-2.5 text-gray-600 active:text-gray-900 transition-colors rounded-lg active:bg-gray-100 touch-manipulation"
                >
                  <Download className="w-5 h-5" />
                </button>
                {user?.role === 'EMPLOYEE' && (
                  (() => {
                    const isRevokedDraft = timesheet?.status === 'DRAFT' && timesheet?.revokedBy != null;
                    const canEditTimesheet = timesheet?.status === 'DRAFT' || 
                                           timesheet?.status === 'PENDING' || 
                                           timesheet?.status === 'REUPDATED' || 
                                           timesheet?.status === 'REJECTED' || 
                                           isRevokedDraft;
                    return canEditTimesheet ? (
                      <button
                        onClick={() => navigate(`/timesheets/${timesheet.id}/edit`)}
                        className="px-4 py-2 bg-green-600 text-white rounded-xl active:bg-green-700 transition-all font-semibold shadow-md active:scale-95 touch-manipulation"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                    ) : null;
                  })()
                )}
                {((user?.role === 'SUPER_ADMIN' || user?.role === 'ADMIN') && timesheet.status !== 'APPROVED' && timesheet.status !== 'REJECTED' && timesheet.status !== 'PENDING') && (
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleApproval('reject')}
                      disabled={rejectMutation.isLoading}
                      className="px-3 py-2 bg-red-600 text-white rounded-xl active:bg-red-700 transition-all disabled:opacity-50 font-semibold shadow-md active:scale-95 touch-manipulation"
                    >
                      <X className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleApproval('approve')}
                      disabled={approveMutation.isLoading}
                      className="px-3 py-2 bg-green-600 text-white rounded-xl active:bg-green-700 transition-all disabled:opacity-50 font-semibold shadow-md active:scale-95 touch-manipulation"
                    >
                      <Check className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Timesheet #{timesheet.id}</h1>
              <p className="text-sm text-gray-600 mt-1">
                {timesheet.employee?.firstName || 'Unknown'} {timesheet.employee?.lastName || 'Employee'}
              </p>
              <p className="text-xs text-gray-500 mt-0.5">
                Week of {safeFormatDate(timesheet.startDate, 'MMM dd, yyyy')}
              </p>
            </div>
          </div>
        </div>

        {/* Mobile Summary Cards */}
        <div className="px-4 pt-4">
          <div className="grid grid-cols-3 gap-3 mb-4">
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
              <div className="text-2xl font-bold text-blue-600">{timesheet.entries?.length || 0}</div>
              <div className="text-xs text-gray-600 font-medium mt-1">Entries</div>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
              <div className="text-2xl font-bold text-green-600">{calculateTotalHours(timesheet.entries).toFixed(1)}h</div>
              <div className="text-xs text-gray-600 font-medium mt-1">Hours</div>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
              <div className="text-2xl font-bold text-purple-600">
                {new Set(timesheet.entries?.map(e => e.project).filter(Boolean)).size || 0}
              </div>
              <div className="text-xs text-gray-600 font-medium mt-1">Projects</div>
            </div>
          </div>

          {/* Status Badge */}
          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 mb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {getStatusIcon(timesheet.status)}
                <span className={`px-3 py-1.5 rounded-full text-xs font-bold ${getStatusColor(timesheet.status)}`}>
                  {timesheet.status}
                </span>
              </div>
              <div className="text-right">
                <div className="text-xs text-gray-500">Avg/Day</div>
                <div className="text-sm font-bold text-gray-900">
                  {(calculateTotalHours(timesheet.entries) / 7).toFixed(1)}h
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Calendar */}
        <div className="px-4">
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="px-4 py-3 bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-gray-200">
              <h2 className="text-base font-bold text-gray-900">Timesheet Calendar</h2>
              <p className="text-xs text-gray-600 mt-0.5">Tap any day to view entries</p>
            </div>
            
            <div className="p-3">
              {(() => {
                const startDate = new Date(timesheet.startDate);
                const endDate = new Date(timesheet.endDate);
                const calendarDays = [];
                const currentDate = new Date(startDate);
                
                const startDayOfWeek = startDate.getDay();
                for (let i = 0; i < startDayOfWeek; i++) {
                  const prevDate = new Date(startDate);
                  prevDate.setDate(startDate.getDate() - (startDayOfWeek - i));
                  calendarDays.push({ date: prevDate, isCurrentWeek: false });
                }
                
                while (currentDate <= endDate) {
                  calendarDays.push({ date: new Date(currentDate), isCurrentWeek: true });
                  currentDate.setDate(currentDate.getDate() + 1);
                }
                
                const endDayOfWeek = endDate.getDay();
                for (let i = endDayOfWeek + 1; i < 7; i++) {
                  const nextDate = new Date(endDate);
                  nextDate.setDate(endDate.getDate() + (i - endDayOfWeek));
                  calendarDays.push({ date: nextDate, isCurrentWeek: false });
                }

                return (
                  <div className="space-y-2">
                    {/* Day Headers */}
                    <div className="grid grid-cols-7 gap-1">
                      {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, idx) => (
                        <div key={`day-header-${idx}`} className="text-center text-xs font-bold text-gray-500 py-1">
                          {day}
                        </div>
                      ))}
                    </div>
                    
                    {/* Calendar Days */}
                    <div className="grid grid-cols-7 gap-1.5">
                      {calendarDays.map((dayData, index) => {
                        const day = dayData.date;
                        const dayEntries = timesheet.entries?.filter(entry => 
                          entry.date && new Date(entry.date).toDateString() === day.toDateString()
                        ) || [];
                        const dayTotal = dayEntries.reduce((sum, entry) => sum + (entry.hours || 0), 0);
                        const isToday = day.toDateString() === new Date().toDateString();
                        const isWeekend = day.getDay() === 0 || day.getDay() === 6;
                        const hasEntries = dayEntries.length > 0;
                        
                        return (
                          <motion.div
                            key={index}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => dayData.isCurrentWeek && handleDayClick(day)}
                            className={`min-h-[80px] p-2 border-2 rounded-xl cursor-pointer touch-manipulation flex flex-col ${
                              dayData.isCurrentWeek 
                                ? hasEntries
                                  ? 'bg-blue-50 border-blue-400 active:bg-blue-100'
                                  : 'bg-white border-gray-300 active:bg-gray-50'
                                : 'bg-gray-50 border-gray-200 opacity-40'
                            } ${isToday ? 'ring-2 ring-blue-500 ring-offset-1' : ''}`}
                          >
                            <div className="flex items-center justify-center mb-1">
                              <span className={`text-sm font-bold ${
                                dayData.isCurrentWeek 
                                  ? isToday ? 'text-blue-600' : isWeekend ? 'text-gray-500' : 'text-gray-900'
                                  : 'text-gray-400'
                              }`}>
                                {day.getDate()}
                              </span>
                            </div>
                            {dayData.isCurrentWeek && dayEntries.length > 0 && (
                              <div className="mt-auto">
                                <div className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-bold mx-auto">
                                  {dayEntries.length}
                                </div>
                              </div>
                            )}
                            {dayData.isCurrentWeek && dayEntries.length === 0 && !isWeekend && (
                              <div className="mt-auto text-xs text-gray-400 text-center">-</div>
                            )}
                          </motion.div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>

        {/* Mobile Info Sections */}
        <div className="px-4 mt-4 space-y-3">
          {/* Employee Info */}
          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
            <h3 className="text-sm font-bold text-gray-900 mb-3">Employee Information</h3>
            <div className="space-y-2">
              <div>
                <span className="text-xs text-gray-500">Name</span>
                <p className="text-sm font-semibold text-gray-900 mt-0.5">
                  {timesheet.employee?.firstName || 'Unknown'} {timesheet.employee?.lastName || 'Employee'}
                </p>
              </div>
              <div>
                <span className="text-xs text-gray-500">Email</span>
                <p className="text-sm font-semibold text-gray-900 mt-0.5 break-all">
                  {timesheet.employee?.email || 'Not available'}
                </p>
              </div>
            </div>
          </div>

          {/* Period Info */}
          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
            <h3 className="text-sm font-bold text-gray-900 mb-3">Timesheet Period</h3>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-xs text-gray-500">Start Date</span>
                <span className="text-sm font-semibold text-gray-900">
                  {new Date(timesheet.startDate).toLocaleDateString()}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-xs text-gray-500">End Date</span>
                <span className="text-sm font-semibold text-gray-900">
                  {new Date(timesheet.endDate).toLocaleDateString()}
                </span>
              </div>
              <div className="flex justify-between pt-2 border-t border-gray-100">
                <span className="text-xs text-gray-500">Total Hours</span>
                <span className="text-sm font-bold text-blue-600">
                  {calculateTotalHours(timesheet.entries)}
                </span>
              </div>
            </div>
          </div>

          {/* Status Info */}
          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
            <h3 className="text-sm font-bold text-gray-900 mb-3">Status Information</h3>
            <div className="space-y-2 text-xs">
              {timesheet.submittedAt && (
                <div>
                  <span className="text-gray-500">Submitted:</span>
                  <span className="ml-2 font-semibold text-gray-900">
                    {new Date(timesheet.submittedAt).toLocaleDateString()} at {new Date(timesheet.submittedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
              )}
              <div>
                <span className="text-gray-500">Created:</span>
                <span className="ml-2 font-semibold text-gray-900">
                  {new Date(timesheet.createdAt).toLocaleDateString()} at {new Date(timesheet.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>
              <div>
                <span className="text-gray-500">Updated:</span>
                <span className="ml-2 font-semibold text-gray-900">
                  {new Date(timesheet.updatedAt).toLocaleDateString()} at {new Date(timesheet.updatedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>
              {timesheet.rejectionReason && (
                <div className="mt-3 p-3 bg-red-50 border-2 border-red-200 rounded-lg">
                  <p className="text-xs font-semibold text-red-800 break-words">
                    Rejection: {timesheet.rejectionReason}
                  </p>
                </div>
              )}
              {timesheet.revocationReason && (
                <div className="mt-3 p-3 bg-orange-50 border-2 border-orange-200 rounded-lg">
                  <p className="text-xs font-semibold text-orange-800 break-words">
                    Revocation: {timesheet.revocationReason}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Mobile Day Details Modal */}
        {showDayDetails && selectedDay && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-end">
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              className="bg-white rounded-t-3xl w-full max-h-[85vh] overflow-hidden flex flex-col"
            >
              <div className="px-4 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50 flex-shrink-0">
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-base font-bold text-gray-900">
                      {selectedDay.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                    </h3>
                    <p className="text-xs text-gray-600 mt-0.5">
                      {(() => {
                        const dayEntries = timesheet.entries?.filter(entry => 
                          entry.date && new Date(entry.date).toDateString() === selectedDay.toDateString()
                        ) || [];
                        const dayTotal = dayEntries.reduce((sum, entry) => sum + (entry.hours || 0), 0);
                        return `${dayEntries.length} entries • ${dayTotal.toFixed(1)} hours`;
                      })()}
                    </p>
                  </div>
                  <button
                    onClick={closeDayDetails}
                    className="p-2 text-gray-600 active:text-gray-900 rounded-lg active:bg-gray-100 touch-manipulation ml-2"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>
              </div>
              
              <div className="flex-1 overflow-y-auto p-4">
                {(() => {
                  const dayEntries = timesheet.entries?.filter(entry => 
                    entry.date && new Date(entry.date).toDateString() === selectedDay.toDateString()
                  ) || [];
                  
                  if (dayEntries.length === 0) {
                    return (
                      <div className="text-center py-12">
                        <Clock className="mx-auto h-12 w-12 text-gray-300" />
                        <h3 className="mt-3 text-sm font-semibold text-gray-900">No entries</h3>
                        <p className="mt-1 text-xs text-gray-500">No time entries for this day</p>
                      </div>
                    );
                  }
                  
                  return (
                    <div className="space-y-3">
                      {dayEntries.map((entry, entryIndex) => (
                        <div
                          key={entryIndex}
                          className="bg-gray-50 border-2 border-gray-200 rounded-xl p-4"
                        >
                          <div className="flex items-start justify-between mb-2">
                            <h4 className="text-base font-bold text-gray-900 flex-1">
                              {entry.project || 'No Project'}
                            </h4>
                            <span className="text-lg font-bold text-green-600 ml-2">
                              {entry.hours.toFixed(1)}h
                            </span>
                          </div>
                          <p className="text-sm text-gray-700 break-words whitespace-pre-wrap">
                            {entry.description || 'No description'}
                          </p>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>
              
              <div className="px-4 py-4 border-t-2 border-gray-200 bg-gray-50 flex-shrink-0">
                <button
                  onClick={closeDayDetails}
                  className="w-full py-3.5 bg-gray-600 text-white rounded-xl active:bg-gray-700 transition-all font-bold shadow-md active:scale-95 touch-manipulation"
                >
                  Close
                </button>
              </div>
            </motion.div>
          </div>
        )}

        {/* Mobile Approval Modal */}
        {showApprovalModal && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white rounded-2xl shadow-xl w-full max-w-sm"
            >
              <div className="px-4 py-4 border-b border-gray-200">
                <h3 className="text-base font-bold text-gray-900">
                  {approvalAction === 'approve' ? 'Approve Timesheet' : 'Reject Timesheet'}
                </h3>
              </div>
              <div className="p-4">
                <p className="text-sm text-gray-600 mb-4">
                  {approvalAction === 'approve' 
                    ? 'Approve this timesheet? This action cannot be undone.'
                    : 'Reject this timesheet? The employee will be notified.'
                  }
                </p>
                {approvalAction === 'reject' && (
                  <div className="mb-4">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Reason *
                    </label>
                    <textarea
                      value={rejectionReason}
                      onChange={(e) => setRejectionReason(e.target.value)}
                      placeholder="Enter rejection reason..."
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 resize-none text-base"
                      rows={4}
                      required
                    />
                  </div>
                )}
                <div className="flex flex-col-reverse gap-2">
                  <button
                    onClick={() => setShowApprovalModal(false)}
                    className="w-full py-3 border-2 border-gray-300 text-gray-700 rounded-xl active:bg-gray-50 transition-all font-semibold active:scale-95 touch-manipulation"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmApproval}
                    disabled={approveMutation.isLoading || rejectMutation.isLoading}
                    className={`w-full py-3.5 text-white rounded-xl transition-all disabled:opacity-50 font-bold shadow-md active:scale-95 touch-manipulation ${
                      approvalAction === 'approve' 
                        ? 'bg-green-600 active:bg-green-700' 
                        : 'bg-red-600 active:bg-red-700'
                    }`}
                  >
                    {approvalAction === 'approve' ? 'Approve' : 'Reject'}
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </div>
    );
  }
  
  // Desktop View (unchanged)
  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-lg shadow-sm p-6"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate(-1)}
              className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex items-center space-x-3">
              <FileText className="w-8 h-8 text-primary-600" />
              <div>
                <div className="flex items-center space-x-3">
                  <h1 className="text-2xl font-bold text-gray-900">Timesheet Details</h1>
                  <span className="px-3 py-1 text-sm font-medium bg-gray-100 text-gray-700 rounded-lg border border-gray-300">
                    ID: {timesheet.id}
                  </span>
                </div>
                <p className="text-gray-600 mt-1">
                  {timesheet.employee?.firstName || 'Unknown'} {timesheet.employee?.lastName || 'Employee'} - Week of {safeFormatDate(timesheet.startDate, 'MMM dd, yyyy')}
                </p>
              </div>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={handleExport}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors flex items-center space-x-2"
            >
              <Download className="w-4 h-4" />
              <span>Export</span>
            </button>
            {/* Edit button for employees - show for DRAFT, PENDING, REUPDATED, REJECTED, and revoked DRAFT */}
            {user?.role === 'EMPLOYEE' && (
              (() => {
                const isRevokedDraft = timesheet?.status === 'DRAFT' && timesheet?.revokedBy != null;
                const canEditTimesheet = timesheet?.status === 'DRAFT' || 
                                       timesheet?.status === 'PENDING' || 
                                       timesheet?.status === 'REUPDATED' || 
                                       timesheet?.status === 'REJECTED' || 
                                       isRevokedDraft;
                return canEditTimesheet ? (
                  <button
                    onClick={() => navigate(`/timesheets/${timesheet.id}/edit`)}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center space-x-2"
                  >
                    <Edit className="w-4 h-4" />
                    <span>Edit</span>
                  </button>
                ) : null;
              })()
            )}
            {((user?.role === 'SUPER_ADMIN' || user?.role === 'ADMIN') && timesheet.status !== 'APPROVED' && timesheet.status !== 'REJECTED' && timesheet.status !== 'PENDING') && (
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handleApproval('reject')}
                  disabled={rejectMutation.isLoading}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center space-x-2"
                >
                  <X className="w-4 h-4" />
                  <span>Reject</span>
                </button>
                <button
                  onClick={() => handleApproval('approve')}
                  disabled={approveMutation.isLoading}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center space-x-2"
                >
                  <Check className="w-4 h-4" />
                  <span>Approve</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </motion.div>

      {/* Timesheet Information */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white rounded-lg shadow-sm p-6"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div>
            <h3 className="text-sm font-medium text-gray-900 mb-2">Employee Information</h3>
            <div className="space-y-2">
               <p className="text-sm text-gray-600">
                 <span className="font-medium">Name:</span> {timesheet.employee?.firstName || 'Unknown'} {timesheet.employee?.lastName || 'Employee'}
               </p>
               <p className="text-sm text-gray-600">
                 <span className="font-medium">Email:</span> {timesheet.employee?.email || 'Not available'}
               </p>
            </div>
          </div>

          <div>
            <h3 className="text-sm font-medium text-gray-900 mb-2">Timesheet Period</h3>
            <div className="space-y-2">
              <p className="text-sm text-gray-600">
                <span className="font-medium">Start Date:</span> {new Date(timesheet.startDate).toLocaleDateString()}
              </p>
              <p className="text-sm text-gray-600">
                <span className="font-medium">End Date:</span> {new Date(timesheet.endDate).toLocaleDateString()}
              </p>
              <p className="text-sm text-gray-600">
                <span className="font-medium">Total Hours:</span> {calculateTotalHours(timesheet.entries)}
              </p>
            </div>
          </div>

          <div>
            <h3 className="text-sm font-medium text-gray-900 mb-2">Status Information</h3>
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                {getStatusIcon(timesheet.status)}
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(timesheet.status)}`}>
                  {timesheet.status}
                </span>
              </div>
              {timesheet.submittedAt && (
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Submitted:</span> {new Date(timesheet.submittedAt).toLocaleDateString()} at {new Date(timesheet.submittedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </p>
              )}
              <p className="text-sm text-gray-600">
                <span className="font-medium">Created:</span> {new Date(timesheet.createdAt).toLocaleDateString()} at {new Date(timesheet.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </p>
              <p className="text-sm text-gray-600">
                <span className="font-medium">Updated:</span> {new Date(timesheet.updatedAt).toLocaleDateString()} at {new Date(timesheet.updatedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </p>
              {timesheet.approvedAt && (
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Approved:</span> {new Date(timesheet.approvedAt).toLocaleDateString()} at {new Date(timesheet.approvedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </p>
              )}
              {timesheet.approvedBy && (
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Approved by:</span> {`${timesheet.approvedBy.firstName || ''} ${timesheet.approvedBy.lastName || ''}`.trim() || 'N/A'}
                  {timesheet.approvedBy.role && (
                    <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-800 align-middle">
                      {typeof timesheet.approvedBy.role === 'object' ? (timesheet.approvedBy.role.name || 'MANAGER') : timesheet.approvedBy.role}
                    </span>
                  )}
                </p>
              )}
              {timesheet.rejectionReason && (
                <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded">
                  <p className="text-sm text-red-800">
                    <span className="font-medium">Rejection Reason:</span> {timesheet.rejectionReason}
                  </p>
                </div>
              )}
              {timesheet.revocationReason && (
                <div className="mt-2 p-2 bg-orange-50 border border-orange-200 rounded">
                  <p className="text-sm text-orange-800">
                    <span className="font-medium">Revocation Reason:</span> {timesheet.revocationReason}
                  </p>
                  {timesheet.revokedBy && (
                    <p className="text-xs text-orange-700 mt-1">
                      Revoked by: {timesheet.revokedBy?.firstName || ''} {timesheet.revokedBy?.lastName || ''}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>

          <div>
            <h3 className="text-sm font-medium text-gray-900 mb-2">Summary</h3>
            <div className="space-y-2">
              <p className="text-sm text-gray-600">
                <span className="font-medium">Entries:</span> {timesheet.entries?.length || 0}
              </p>
              <p className="text-sm text-gray-600">
                <span className="font-medium">Projects:</span> {new Set(timesheet.entries?.map(e => e.project).filter(Boolean)).size || 0}
              </p>
              <p className="text-sm text-gray-600">
                <span className="font-medium">Avg Hours/Day:</span> {(calculateTotalHours(timesheet.entries) / 7).toFixed(1)}
              </p>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Interactive Calendar View */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-white rounded-lg shadow-sm border"
      >
        {/* Calendar Header */}
        <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Timesheet Calendar</h2>
              <p className="text-sm text-gray-600">Click on any day to view detailed entries</p>
            </div>
            <div className="text-right">
              <div className="text-sm text-gray-500">Total Hours</div>
              <div className="text-xl font-bold text-blue-600">
                {calculateTotalHours(timesheet.entries).toFixed(1)}h
              </div>
            </div>
          </div>
        </div>
        
        {/* Calendar Grid */}
        <div className="p-6">
          {(() => {
            const startDate = new Date(timesheet.startDate);
            const endDate = new Date(timesheet.endDate);
            
            // Create calendar grid
            const calendarDays = [];
            const currentDate = new Date(startDate);
            
            // Add days before the start date to fill the week
            const startDayOfWeek = startDate.getDay();
            for (let i = 0; i < startDayOfWeek; i++) {
              const prevDate = new Date(startDate);
              prevDate.setDate(startDate.getDate() - (startDayOfWeek - i));
              calendarDays.push({ date: prevDate, isCurrentWeek: false });
            }
            
            // Add the actual week days
            while (currentDate <= endDate) {
              calendarDays.push({ date: new Date(currentDate), isCurrentWeek: true });
              currentDate.setDate(currentDate.getDate() + 1);
            }
            
            // Add remaining days to complete the week
            const endDayOfWeek = endDate.getDay();
            for (let i = endDayOfWeek + 1; i < 7; i++) {
              const nextDate = new Date(endDate);
              nextDate.setDate(endDate.getDate() + (i - endDayOfWeek));
              calendarDays.push({ date: nextDate, isCurrentWeek: false });
            }

            return (
              <div className="space-y-4">
                {/* Calendar Header */}
                <div className="grid grid-cols-7 gap-1">
                  {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, idx) => (
                    <div key={`day-header-${idx}`} className="p-3 text-center text-sm font-medium text-gray-500 bg-gray-50 rounded-lg">
                      {day}
                    </div>
                  ))}
                </div>
                
                {/* Calendar Grid */}
                <div className="grid grid-cols-7 gap-1">
                  {calendarDays.map((dayData, index) => {
                    const day = dayData.date;
                    const dayEntries = timesheet.entries?.filter(entry => 
                      entry.date && new Date(entry.date).toDateString() === day.toDateString()
                    ) || [];
                    
                    const dayTotal = dayEntries.reduce((sum, entry) => sum + (entry.hours || 0), 0);
                    const isToday = day.toDateString() === new Date().toDateString();
                    const isWeekend = day.getDay() === 0 || day.getDay() === 6;
                    const hasEntries = dayEntries.length > 0;
                    
                    return (
                      <motion.div
                        key={index}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => dayData.isCurrentWeek && handleDayClick(day)}
                        className={`min-h-[140px] p-3 border rounded-lg cursor-pointer transition-all duration-200 ${
                          dayData.isCurrentWeek 
                            ? hasEntries
                              ? 'bg-blue-50 border-blue-200 hover:bg-blue-100 hover:border-blue-300'
                              : 'bg-white border-gray-200 hover:bg-gray-50 hover:border-gray-300'
                            : 'bg-gray-50 border-gray-100 cursor-not-allowed opacity-50'
                        } ${isToday ? 'ring-2 ring-blue-500' : ''}`}
                      >
                        {/* Date Header */}
                        <div className="flex items-center justify-between mb-2">
                          <span className={`text-sm font-medium ${
                            dayData.isCurrentWeek 
                              ? isToday 
                                ? 'text-blue-600' 
                                : isWeekend 
                                  ? 'text-gray-500' 
                                  : 'text-gray-900'
                              : 'text-gray-400'
                          }`}>
                            {day.getDate()}
                          </span>
                          {dayTotal > 0 && (
                            <span className="text-xs font-medium text-green-600 bg-green-100 px-2 py-1 rounded-full">
                              {dayTotal.toFixed(1)}h
                            </span>
                          )}
                        </div>
                        
                        {/* Entries Preview */}
                        {dayData.isCurrentWeek && dayEntries.length > 0 && (
                          <div className="space-y-1">
                            {dayEntries.slice(0, 2).map((entry, entryIndex) => (
                              <div
                                key={entryIndex}
                                className="text-xs p-2 bg-white border border-blue-200 rounded truncate"
                                title={`${entry.project || 'No Project'}: ${entry.hours.toFixed(1)}h - ${entry.description || 'No description'}`}
                              >
                                <div className="font-medium text-blue-800 truncate">
                                  {entry.project || 'No Project'}
                                </div>
                                <div className="text-blue-600">
                                  {entry.hours.toFixed(1)}h
                                </div>
                              </div>
                            ))}
                            {dayEntries.length > 2 && (
                              <div className="text-xs text-gray-500 text-center bg-gray-100 rounded p-1">
                                +{dayEntries.length - 2} more
                              </div>
                            )}
                          </div>
                        )}
                        
                        {dayData.isCurrentWeek && dayEntries.length === 0 && (
                          <div className="text-xs text-gray-400 text-center mt-4">
                            {isWeekend ? 'Weekend' : 'No entries'}
                          </div>
                        )}
                        
                        {/* Click indicator */}
                        {dayData.isCurrentWeek && (
                          <div className="text-xs text-gray-400 text-center mt-2">
                            Click to view details
                          </div>
                        )}
                      </motion.div>
                    );
                  })}
                </div>
              </div>
            );
          })()}
        </div>

        {/* Summary Stats */}
        <div className="border-t border-gray-200 px-6 py-4 bg-gray-50">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">
                {timesheet.entries?.length || 0}
              </div>
              <div className="text-sm text-gray-600">Total Entries</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">
                {calculateTotalHours(timesheet.entries).toFixed(1)}h
              </div>
              <div className="text-sm text-gray-600">Total Hours</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-600">
                {new Set(timesheet.entries?.map(e => e.project).filter(Boolean)).size || 0}
              </div>
              <div className="text-sm text-gray-600">Projects</div>
            </div>
          </div>
        </div>

        {timesheet.entries?.length === 0 && (
          <div className="px-6 py-12 text-center">
            <Clock className="mx-auto h-12 w-12 text-gray-300" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No time entries</h3>
            <p className="mt-1 text-sm text-gray-500">This timesheet has no time entries recorded.</p>
          </div>
        )}
      </motion.div>

      {/* Day Details Modal */}
      {showDayDetails && selectedDay && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden"
          >
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {selectedDay.toLocaleDateString('en-US', { 
                      weekday: 'long', 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {(() => {
                      const dayEntries = timesheet.entries?.filter(entry => 
                        entry.date && new Date(entry.date).toDateString() === selectedDay.toDateString()
                      ) || [];
                      const dayTotal = dayEntries.reduce((sum, entry) => sum + (entry.hours || 0), 0);
                      return `${dayEntries.length} entries • ${dayTotal.toFixed(1)} hours`;
                    })()}
                  </p>
                </div>
                <button
                  onClick={closeDayDetails}
                  className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            {/* Modal Content */}
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              {(() => {
                const dayEntries = timesheet.entries?.filter(entry => 
                  entry.date && new Date(entry.date).toDateString() === selectedDay.toDateString()
                ) || [];
                
                if (dayEntries.length === 0) {
                  return (
                    <div className="text-center py-8">
                      <Clock className="mx-auto h-12 w-12 text-gray-300" />
                      <h3 className="mt-2 text-sm font-medium text-gray-900">No entries for this day</h3>
                      <p className="mt-1 text-sm text-gray-500">No time entries were recorded on this day.</p>
                    </div>
                  );
                }
                
                return (
                  <div className="space-y-4">
                    {dayEntries.map((entry, entryIndex) => (
                      <motion.div
                        key={entryIndex}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: entryIndex * 0.1 }}
                        className="bg-gray-50 border border-gray-200 rounded-lg p-4"
                      >
                        <div className="flex items-start justify-between mb-3">
                          <h4 className="font-medium text-gray-900 text-lg break-words">
                            {entry.project || 'No Project'}
                          </h4>
                          <span className="text-lg font-bold text-green-600 flex-shrink-0 ml-2">
                            {entry.hours.toFixed(1)}h
                          </span>
                        </div>
                        
                        <div className="text-sm text-gray-700 mb-3">
                          {(() => {
                            const description = entry.description || 'No description provided';
                            // Check if description contains bullet points (lines starting with • or -)
                            const lines = description.split('\n').filter(line => line.trim());
                            const hasBullets = lines.some(line => line.trim().match(/^[•\-\*]/));
                            
                            if (hasBullets) {
                              // Render as bullet list
                              return (
                                <ul className="list-none space-y-1 ml-2">
                                  {lines.map((line, idx) => {
                                    const cleanLine = line.trim().replace(/^[•\-\*]\s*/, '');
                                    return cleanLine ? (
                                      <li key={idx} className="flex items-start">
                                        <span className="text-primary-600 mr-2 flex-shrink-0">•</span>
                                        <span className="break-words flex-1">{cleanLine}</span>
                                      </li>
                                    ) : null;
                                  })}
                                </ul>
                              );
                            } else {
                              // Render as paragraph
                              return (
                                <p className="break-words whitespace-pre-wrap overflow-wrap-anywhere">
                                  {description}
                                </p>
                              );
                            }
                          })()}
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                          {entry.taskName && (
                            <div className="break-words">
                              <span className="font-medium text-gray-600">Task:</span>
                              <span className="ml-2 text-gray-900 break-words">{entry.taskName}</span>
                            </div>
                          )}
                          {(entry.startTime || entry.endTime) && (
                            <div>
                              <span className="font-medium text-gray-600">Time:</span>
                              <span className="ml-2 text-gray-900">
                                {entry.startTime && entry.endTime 
                                  ? `${entry.startTime} - ${entry.endTime}`
                                  : entry.startTime || entry.endTime
                                }
                              </span>
                            </div>
                          )}
                        </div>
                        
                        {entry.notes && (
                          <div className="mt-3 p-3 bg-yellow-50 border-l-4 border-yellow-300 rounded">
                            <p className="text-sm text-yellow-800">
                              <strong>Note:</strong> {entry.notes}
                            </p>
                          </div>
                        )}
                      </motion.div>
                    ))}
                  </div>
                );
              })()}
            </div>
            
            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
              <div className="flex justify-end">
                <button
                  onClick={closeDayDetails}
                  className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Approval Modal */}
      {showApprovalModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-lg shadow-xl max-w-md w-full"
          >
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">
                {approvalAction === 'approve' ? 'Approve Timesheet' : 'Reject Timesheet'}
              </h3>
            </div>
            <div className="p-6">
              <p className="text-sm text-gray-600 mb-4">
                {approvalAction === 'approve' 
                  ? 'Are you sure you want to approve this timesheet? This action cannot be undone.'
                  : 'Are you sure you want to reject this timesheet? The employee will be notified and can resubmit.'
                }
              </p>
              
              {approvalAction === 'reject' && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Reason for Rejection *
                  </label>
                  <textarea
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    placeholder="Please provide a detailed reason for rejecting this timesheet..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 resize-none"
                    rows={3}
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    This reason will be visible to the employee when they view their timesheet.
                  </p>
                </div>
              )}
              
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowApprovalModal(false)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmApproval}
                  disabled={approveMutation.isLoading || rejectMutation.isLoading}
                  className={`px-4 py-2 text-white rounded-lg transition-colors disabled:opacity-50 flex items-center space-x-2 ${
                    approvalAction === 'approve' 
                      ? 'bg-green-600 hover:bg-green-700' 
                      : 'bg-red-600 hover:bg-red-700'
                  }`}
                >
                  {(approveMutation.isLoading || rejectMutation.isLoading) && (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  )}
                  <span>
                    {approvalAction === 'approve' ? 'Approve' : 'Reject'}
                  </span>
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default TimesheetDetails;